// Caja de escribir del chat de equipo (pantalla completa, estilo Rocket.Chat).
//
// Todo lo que sale de aquí sube por los callbacks del padre (onEnviar / onAdjuntar / onAudio):
// este componente NO habla con la API, así que sirve igual para un canal, un DM o soporte.
//
// Marcadores de formato: se usa Markdown estándar (**negrita**, _cursiva_, ~~tachado~~, `código`)
// porque es lo que el pintor de mensajes de la app ya entiende hoy para la negrita, y así lo que
// escribe el usuario se ve igual en el hilo y no queda texto con asteriscos sueltos.
import { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react'
import Icon from '../Icon'
import { useToast } from '../../context/ToastContext'
import { msgError } from '../../utils/errores'
import { MAX_MB } from './limites'   // T6: el límite de tamaño vive en UN solo sitio
import usePantallaDespierta from '../../hooks/usePantallaDespierta'
import { ALIAS_TODOS, detectarMencion } from '../../utils/mencionArroba'   // el MISMO criterio de arroba que el servidor
import { nombreRol } from '../../utils/nombreRol'
import { t as i18nT, tx, marca, tDato } from '../../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)
// B-868 · el tamaño del perfil es un zoom en <html> (utils/uiScale): lo medido en la pantalla (ratón, cajas, ventana)
// va en px de pantalla y lo que se escribe en un estilo se pinta multiplicado por el zoom; aquí se pasa a px de estilo.
// Sin import a propósito: las guardias montan este fichero con sus dobles.
const zoomDeLaPagina = () => { try { return parseFloat(document.documentElement.style.zoom) || 1 } catch (_) { return 1 } }

const MAX_TEXTO = 4000            // el backend corta a 4000: avisamos antes en vez de perder texto
const MAX_AUDIO_SEG = 300         // corte de seguridad: una nota de voz no debería pasar de 5 min
const LINEA = 22                  // alto de línea del textarea (px)
const LINEAS_MAX = 6              // a partir de aquí, scroll dentro del textarea

// Emojis para el panel propio (sin dependencias externas). Son CONTENIDO del mensaje, no UI.
const CATEGORIAS = [
  [marca('components.chat.ComposerChat.caras', 'Caras'), '😀 😃 😄 😁 😆 😅 😂 🙂 😉 😊 😍 😘 🤗 🤔 😐 😴 😪 😥 😢 😭 😤 😠 😳 😬 🙄 😷 🤒 🤕 🥳 😎 🤓 🤯'],
  [marca('components.chat.ComposerChat.gestos', 'Gestos'), '👍 👎 👌 ✌️ 🤞 👏 🙌 🙏 👋 🤝 💪 ☝️ 👆 👇 ✍️ 🫶'],
  [marca('components.chat.ComposerChat.animo', 'Ánimo'), '❤️ 🧡 💛 💚 💙 💜 🖤 🤍 💔 ❣️ 💯 ✨ ⭐ 🔥 🎉 🎊 🍀 ☀️'],
  [marca('components.chat.ComposerChat.trabajo', 'Trabajo'), '✅ ❌ ⚠️ ❗ ❓ 📌 📎 📅 🕐 ⏰ 💬 📞 📷 🔔 🔕 🔒 💡 🎯 🚀 📄 📁 💻 🖨️ 💰 💳 🦷 💊 🩺 🚑 🧼 🧾 📦'],
].map(([t, s]) => [t, s.split(' ')])

const CLAVE_RECIENTES = 'chat-emojis-recientes'
// El contenido viene de localStorage, que es de fuera: se sanea SIEMPRE. Un valor que no sea texto
// reventaría el pintado ("Objects are not valid as a React child") y los repetidos darían dos botones
// con la misma key. Nada de esto puede tumbar la caja de escribir.
const leerRecientes = () => {
  try {
    const v = JSON.parse(localStorage.getItem(CLAVE_RECIENTES) || '[]')
    return Array.isArray(v) ? [...new Set(v.filter(x => typeof x === 'string' && x))].slice(0, 16) : []
  } catch { return [] }
}

// Iconos de formato que no existen en components/Icon.jsx (ese fichero es de otra pieza; aquí se
// dibujan los que faltan para no bloquear la pantalla esperando a que se añadan allí).
const TRAZO = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.9, strokeLinecap: 'round', strokeLinejoin: 'round' }
const FORMAS = {
  smile: <><circle cx="12" cy="12" r="10" /><path d="M8 14.5s1.6 2 4 2 4-2 4-2" /><line x1="9" y1="9.5" x2="9.01" y2="9.5" /><line x1="15" y1="9.5" x2="15.01" y2="9.5" /></>,
  bold: <><path d="M7 4h6a4 4 0 0 1 0 8H7z" /><path d="M7 12h7a4 4 0 0 1 0 8H7z" /></>,
  italic: <><line x1="19" y1="4" x2="10" y2="4" /><line x1="14" y1="20" x2="5" y2="20" /><line x1="15" y1="4" x2="9" y2="20" /></>,
  strike: <><path d="M16.5 5.5A4.5 4.5 0 0 0 13 4h-1.5A3.5 3.5 0 0 0 8.6 9.6" /><path d="M13.5 14.2A3.4 3.4 0 0 1 12.5 20H11a4.5 4.5 0 0 1-3.5-1.5" /><line x1="3.5" y1="12" x2="20.5" y2="12" /></>,
  code: <><polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" /></>,
  at: <><circle cx="12" cy="12" r="4" /><path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-3.9 7.9" /></>,
}
const IcoFmt = ({ n, s = 18 }) => <svg width={s} height={s} viewBox="0 0 24 24" style={{ display: 'block' }} {...TRAZO}>{FORMAS[n] || null}</svg>

const sinAcentos = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
const mmss = (t) => `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`
// Extracto legible del mensaje al que se responde (puede ser un adjunto sin texto).
const extracto = (m) => {
  if (!m) return ''
  const t = m.texto || m.transcripcion
  if (t) return String(t).replace(/\s+/g, ' ').slice(0, 140)
  if (m.adjunto_nombre) return i18nT('components.chat.ComposerChat.adjunto', 'Adjunto: {{adjunto_nombre}}', { adjunto_nombre: m.adjunto_nombre })
  return m.tipo === 'imagen' ? i18nT('components.chat.ComposerChat.imagen', 'Imagen') : m.tipo === 'audio' ? i18nT('components.chat.ComposerChat.nota_de_voz', 'Nota de voz') : i18nT('components.chat.ComposerChat.adjunto_2', 'Adjunto')
}

// B-207 · La entrada "@todos" de la lista. No es una persona, así que va aparte y con su propio
// id: lo que inserta es el alias literal, y el servidor lo expande a la gente con acceso al canal.
// (El alias en sí sale del criterio compartido: utils/mencionArroba.)
const ID_TODOS = '__todos__'

export default function ComposerChat({
  valor = '', setValor,
  respondiendo = null, cancelarResponder,
  onEnviar, onAdjuntar, onAudio,
  // LOS QUE PUEDEN SER MENCIONADOS. Ojo: esta lista tiene que ser la de la gente CON ACCESO A ESE
  // GRUPO (la que sirve /chat/canales/:id/menciones), no la clínica entera. Ofrecer mencionar a
  // quien no puede abrir la conversación es prometer un aviso que no va a llegar a ninguna parte.
  usuarios = [],
  // { n } — a cuánta gente iría un @todos. Se enseña ANTES de mandarlo para que no sea un accidente.
  // Sin este dato la fila @todos no se ofrece: un "avisar a todos" sin decir a cuántos es una trampa.
  todos = null,
  deshabilitado = false,
}) {
  const toast = useToast()
  const taRef = useRef(null)
  const fileRef = useRef(null)
  const emojiRef = useRef(null)      // panel de emojis
  const btnEmojiRef = useRef(null)   // su botón (para no cerrarlo con su propio clic)
  // Posición del cursor a restaurar DESPUÉS de que el padre nos devuelva el valor nuevo: sin esto,
  // envolver la selección o meter un emoji deja el cursor al final y se pierde el hilo de escritura.
  const caretRef = useRef(null)

  const [emojisAbierto, setEmojisAbierto] = useState(false)
  const [catEmoji, setCatEmoji] = useState(0)
  const [recientes, setRecientes] = useState(leerRecientes)
  const [arrastrando, setArrastrando] = useState(false)
  const arrastreRef = useRef(0)      // dragenter/dragleave anidados: contamos para no parpadear
  const [mencion, setMencion] = useState(null)   // { inicio, q } mientras se escribe una @mención
  const [idxMencion, setIdxMencion] = useState(0)

  // Enter envía en escritorio; en móvil (teclado virtual) Enter hace salto y se envía con el botón.
  const [ancho, setAncho] = useState(() => (typeof window !== 'undefined' ? window.innerWidth / zoomDeLaPagina() : 1024))
  useEffect(() => {
    const h = () => setAncho(window.innerWidth / zoomDeLaPagina())
    window.addEventListener('resize', h)
    window.addEventListener('biango:zoom', h)
    return () => { window.removeEventListener('resize', h); window.removeEventListener('biango:zoom', h) }
  }, [])
  const esMovil = ancho < 768

  // ── Textarea que crece con el contenido ─────────────────────────────────────
  useLayoutEffect(() => {
    const el = taRef.current
    if (!el) return
    el.style.height = 'auto'
    const max = LINEAS_MAX * LINEA + 20
    el.style.height = Math.min(el.scrollHeight, max) + 'px'
    el.style.overflowY = el.scrollHeight > max ? 'auto' : 'hidden'
  }, [valor])

  // Restaurar foco y selección tras un cambio de valor provocado por nosotros (formato/emoji/mención).
  useLayoutEffect(() => {
    const c = caretRef.current
    if (!c) return
    caretRef.current = null
    const el = taRef.current
    if (!el) return
    el.focus()
    try { el.setSelectionRange(c[0], c[1]) } catch { /* el textarea puede haberse desmontado */ }
  }, [valor])

  // Al empezar a responder a alguien, el foco va a la caja (si no, hay que ir a buscarla con el ratón).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (respondiendo) taRef.current?.focus() }, [respondiendo?.id])

  // ── Menciones ───────────────────────────────────────────────────────────────
  // CUÁNDO se abre la lista lo decide `utils/mencionArroba`, que es el MISMO criterio que aplica el
  // servidor para saber a quién avisa. Estaba escrito aquí con una regla más estricta —principio de
  // línea, espacio o paréntesis— y por eso «Recordatorio:@todos» no desplegaba nada mientras el
  // servidor sí avisaba a todo el grupo: quien escribía no veía ni la lista ni el número.
  const sugeridos = useMemo(() => {
    if (!mencion) return []
    const q = sinAcentos(mencion.q)
    const lista = (usuarios || []).filter(u => u && u.nombre)
    // @todos SIEMPRE EL PRIMERO cuando casa con lo tecleado. Lleva pegado a cuánta gente va: eso es
    // lo que impide que sea un accidente, y por eso no se ofrece si no sabemos el número.
    const nTodos = todos && Number(todos.n) > 0 ? Number(todos.n) : 0
    const filaTodos = nTodos && ALIAS_TODOS.startsWith(q)
      ? [{ id: ID_TODOS, esTodos: true, nombre: ALIAS_TODOS, n: nTodos }] : []
    if (!q) return [...filaTodos, ...lista.slice(0, 8)]
    // Primero los que empiezan por lo tecleado (nombre o apellido), luego el resto.
    const casa = (u) => sinAcentos(u.nombre).split(/\s+/).some(p => p.startsWith(q))
    return [...filaTodos, ...lista.filter(casa), ...lista.filter(u => !casa(u) && sinAcentos(u.nombre).includes(q))].slice(0, 9)
  }, [mencion, usuarios, todos])
  const mencionAbierta = !!mencion && sugeridos.length > 0
  // El resaltado se acota al pintar: si la lista se acorta al teclear, el índice guardado se sale.
  const idxSel = sugeridos.length ? Math.min(idxMencion, sugeridos.length - 1) : 0

  // Cambiar la mención y, SOLO si cambió lo tecleado, volver al primer resultado. Se hace aquí y no
  // en un efecto porque el cursor se recalcula también al soltar las flechas: un efecto machacaría
  // la fila que el usuario acaba de elegir con el teclado.
  const ponerMencion = (m) => {
    setMencion(m)
    if ((m ? m.q : null) !== (mencion ? mencion.q : null)) setIdxMencion(0)
  }

  const insertarMencion = (u) => {
    const el = taRef.current
    const caret = el ? el.selectionStart : valor.length
    const ini = mencion ? mencion.inicio : caret
    // Para @todos se inserta el alias tal cual; el servidor es quien lo expande a la gente con
    // acceso al canal (aquí no se resuelve nada: la lista de la pantalla puede estar caducada).
    const trozo = '@' + (u.esTodos ? ALIAS_TODOS : u.nombre) + ' '
    const nuevo = valor.slice(0, ini) + trozo + valor.slice(caret)
    const pos = ini + trozo.length
    ponerMencion(null)
    caretRef.current = [pos, pos]
    setValor(nuevo.slice(0, MAX_TEXTO))
  }

  // ── Escritura ───────────────────────────────────────────────────────────────
  const alEscribir = (e) => {
    const el = e.target
    const v = el.value
    if (v.length > MAX_TEXTO) {
      toast.error(i18nT('components.chat.ComposerChat.el_mensaje_no_puede_pasar', 'El mensaje no puede pasar de {{MAX_TEXTO}} caracteres', { MAX_TEXTO }))
      // Pasado el tope, el recorte es IGUAL al estado que ya teníamos: React no vuelve a pintar y el
      // <textarea> se quedaba con el texto de más, que luego no se enviaba. Hay que devolverle el
      // recorte a mano (conservando el cursor) para que lo que se ve sea lo que se manda.
      const pos = Math.min(el.selectionStart, MAX_TEXTO)
      el.value = v.slice(0, MAX_TEXTO)
      try { el.setSelectionRange(pos, pos) } catch { /* navegador quisquilloso: da igual, el texto ya está bien */ }
    }
    setValor(el.value.slice(0, MAX_TEXTO))
    ponerMencion(detectarMencion(el.value, el.selectionStart))
  }
  // El cursor también se mueve con clic o flechas: hay que reevaluar si sigue dentro de una @mención.
  const sincronizarCursor = (e) => ponerMencion(detectarMencion(e.target.value, e.target.selectionStart))

  const enviar = () => {
    if (deshabilitado || !String(valor || '').trim()) return
    ponerMencion(null); setEmojisAbierto(false)
    onEnviar?.()
    taRef.current?.focus()
  }

  const alPulsarTecla = (e) => {
    if (mencionAbierta) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setIdxMencion((idxSel + 1) % sugeridos.length); return }
      if (e.key === 'ArrowUp') { e.preventDefault(); setIdxMencion((idxSel - 1 + sugeridos.length) % sugeridos.length); return }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertarMencion(sugeridos[idxSel]); return }
      if (e.key === 'Escape') { e.preventDefault(); ponerMencion(null); return }
    }
    if (e.key === 'Escape') {
      if (emojisAbierto) { e.preventDefault(); setEmojisAbierto(false); return }
      if (respondiendo) { e.preventDefault(); cancelarResponder?.(); return }
    }
    // Atajos de toda la vida para negrita y cursiva.
    if ((e.ctrlKey || e.metaKey) && !e.altKey) {
      const k = e.key.toLowerCase()
      if (k === 'b') { e.preventDefault(); envolver('**'); return }
      if (k === 'i') { e.preventDefault(); envolver('_'); return }
    }
    if (e.key === 'Enter' && !e.shiftKey && !esMovil) { e.preventDefault(); enviar() }
  }

  // ── Formato: envuelve la SELECCIÓN y deja el cursor donde estaba ─────────────
  const envolver = (izq, der = izq) => {
    if (deshabilitado) return
    const el = taRef.current
    const ini = el ? el.selectionStart : valor.length
    const fin = el ? el.selectionEnd : valor.length
    const sel = valor.slice(ini, fin)
    const yaEnvuelto = valor.slice(Math.max(0, ini - izq.length), ini) === izq && valor.slice(fin, fin + der.length) === der
    if (yaEnvuelto) {
      // Segundo clic sobre el mismo formato = quitarlo (como en cualquier editor).
      const nuevo = valor.slice(0, ini - izq.length) + sel + valor.slice(fin + der.length)
      caretRef.current = [ini - izq.length, fin - izq.length]
      setValor(nuevo)
      return
    }
    const nuevo = valor.slice(0, ini) + izq + sel + der + valor.slice(fin)
    if (nuevo.length > MAX_TEXTO) return toast.error(i18nT('components.chat.ComposerChat.el_mensaje_no_puede_pasar', 'El mensaje no puede pasar de {{MAX_TEXTO}} caracteres', { MAX_TEXTO }))
    // Con texto seleccionado se mantiene seleccionado; sin selección, el cursor queda entre marcas.
    caretRef.current = sel ? [ini + izq.length, fin + izq.length] : [ini + izq.length, ini + izq.length]
    setValor(nuevo)
  }

  const insertarTexto = (txt) => {
    const el = taRef.current
    const ini = el ? el.selectionStart : valor.length
    const fin = el ? el.selectionEnd : valor.length
    const nuevo = valor.slice(0, ini) + txt + valor.slice(fin)
    if (nuevo.length > MAX_TEXTO) return toast.error(i18nT('components.chat.ComposerChat.el_mensaje_no_puede_pasar', 'El mensaje no puede pasar de {{MAX_TEXTO}} caracteres', { MAX_TEXTO }))
    caretRef.current = [ini + txt.length, ini + txt.length]
    setValor(nuevo)
  }

  // Botón "@": mete la arroba y abre la lista ya, sin esperar a que se teclee una letra. El índice
  // se calcula ANTES de insertar (después, el valor nuevo aún no ha llegado desde el padre).
  const abrirMenciones = () => {
    if (deshabilitado || valor.length >= MAX_TEXTO) return
    const el = taRef.current
    const ini = el ? el.selectionStart : valor.length
    insertarTexto('@')
    ponerMencion({ inicio: ini, q: '' })
  }

  const elegirEmoji = (em) => {
    insertarTexto(em)
    setRecientes(prev => {
      const lista = [em, ...prev.filter(x => x !== em)].slice(0, 16)
      try { localStorage.setItem(CLAVE_RECIENTES, JSON.stringify(lista)) } catch { /* modo privado: sin recientes */ }
      return lista
    })
  }

  // Cerrar el panel de emojis al pulsar fuera (el propio botón se ignora: si no, no se podría cerrar).
  useEffect(() => {
    if (!emojisAbierto) return
    const fuera = (e) => {
      if (emojiRef.current?.contains(e.target) || btnEmojiRef.current?.contains(e.target)) return
      setEmojisAbierto(false)
    }
    document.addEventListener('mousedown', fuera)
    return () => document.removeEventListener('mousedown', fuera)
  }, [emojisAbierto])

  // ── Adjuntos: botón, arrastrar y soltar, y pegar desde el portapapeles ──────
  const subir = async (ficheros) => {
    const lista = Array.from(ficheros || []).filter(Boolean)
    if (!lista.length || !onAdjuntar) return
    for (const f of lista) {
      if (f.size > MAX_MB * 1024 * 1024) { toast.error(i18nT('components.chat.ComposerChat.supera_el_maximo_de_mb', '"{{name}}" supera el máximo de {{MAX_MB}} MB', { name: f.name, MAX_MB })); continue }
      // Uno detrás de otro (si onAdjuntar devuelve promesa) para no lanzar varias subidas a la vez.
      try { await onAdjuntar(f) } catch (e) { toast.error(msgError(e, i18nT('components.chat.ComposerChat.no_se_pudo_adjuntar_el', 'No se pudo adjuntar el archivo'))) }
    }
  }
  const traeArchivos = (e) => Array.from(e.dataTransfer?.types || []).includes('Files')
  const alSoltar = (e) => {
    if (deshabilitado) return
    e.preventDefault()
    arrastreRef.current = 0; setArrastrando(false)
    subir(e.dataTransfer?.files)
  }
  const alPegar = (e) => {
    const items = Array.from(e.clipboardData?.items || []).filter(i => i.kind === 'file')
    if (!items.length || deshabilitado) return
    const ficheros = items.map(i => i.getAsFile()).filter(Boolean)
    if (!ficheros.length) return
    e.preventDefault()   // si no, la imagen pegada también entraría como texto basura
    subir(ficheros)
  }

  // ── Nota de voz ─────────────────────────────────────────────────────────────
  // Si el navegador no trae MediaRecorder, el botón NO se pinta: no hay error que enseñar, es que
  // ese navegador no puede grabar (nada que ver con el servidor).
  const soportaAudio = typeof window !== 'undefined' && !!window.MediaRecorder && !!navigator.mediaDevices?.getUserMedia
  const [grabando, setGrabando] = useState(false)
  // La pantalla no se apaga mientras se graba: al apagarse, el movil corta el microfono.
  usePantallaDespierta(grabando)
  const [segs, setSegs] = useState(0)
  const mediaRef = useRef(null)
  const streamRef = useRef(null)
  const trozosRef = useRef([])
  const canceladoRef = useRef(false)
  // ¿Seguimos en pantalla? getUserMedia tarda lo que el usuario tarde en dar el permiso: si mientras
  // tanto se cambia de conversación o de página, el componente ya no existe y su limpieza de
  // desmontaje YA se ejecutó — sin esta bandera arrancábamos la grabación después y el micrófono se
  // quedaba abierto para siempre (punto rojo del navegador incluido).
  const montadoRef = useRef(true)

  const soltarMicro = () => {
    try { streamRef.current?.getTracks().forEach(t => t.stop()) } catch { /* ya cerrado */ }
    streamRef.current = null
  }
  const empezarAudio = async () => {
    if (deshabilitado || grabando) return
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      if (!montadoRef.current) { stream.getTracks().forEach(t => t.stop()); return }
      streamRef.current = stream
      const mime = window.MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm'
        : (window.MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : '')
      const mr = new window.MediaRecorder(stream, mime ? { mimeType: mime } : undefined)
      trozosRef.current = []; canceladoRef.current = false
      mr.ondataavailable = (ev) => { if (ev.data?.size) trozosRef.current.push(ev.data) }
      mr.onstop = () => {
        soltarMicro()
        const tipo = mr.mimeType || mime || 'audio/webm'
        const blob = new Blob(trozosRef.current, { type: tipo })
        trozosRef.current = []
        if (!canceladoRef.current && blob.size) Promise.resolve(onAudio?.(blob)).catch(e => toast.error(msgError(e, i18nT('components.chat.ComposerChat.no_se_pudo_enviar_la', 'No se pudo enviar la nota de voz'))))
      }
      mediaRef.current = mr
      mr.start()
      setSegs(0); setGrabando(true)
    } catch {
      // Permiso denegado o micrófono ocupado: es del navegador, no del servidor.
      toast.error(i18nT('components.chat.ComposerChat.no_se_pudo_acceder_al', 'No se pudo acceder al micrófono. Revisa el permiso del navegador.'))
      soltarMicro()
    }
  }
  const pararAudio = () => {
    canceladoRef.current = false
    try { if (mediaRef.current?.state !== 'inactive') mediaRef.current?.stop() } catch { /* nada que parar */ }
    setGrabando(false)
  }
  const cancelarAudio = () => {
    canceladoRef.current = true
    try { if (mediaRef.current?.state !== 'inactive') mediaRef.current?.stop() } catch { /* nada que parar */ }
    trozosRef.current = []
    soltarMicro()
    setGrabando(false)
  }
  // Contador de la grabación. El corte de seguridad vive DENTRO del temporizador: si alguien se deja
  // el micrófono abierto, a los 5 minutos se envía lo grabado y se cierra solo.
  useEffect(() => {
    if (!grabando) return
    let n = 0
    const t = setInterval(() => {
      n += 1
      setSegs(n)
      if (n >= MAX_AUDIO_SEG) pararAudio()
    }, 1000)
    return () => clearInterval(t)
  }, [grabando])
  // Al desmontar (cambio de pantalla) hay que soltar el micrófono sí o sí, o queda el punto rojo.
  useEffect(() => {
    montadoRef.current = true
    return () => {
      montadoRef.current = false
      canceladoRef.current = true
      try { if (mediaRef.current?.state !== 'inactive') mediaRef.current?.stop() } catch { /* ya parado */ }
      try { streamRef.current?.getTracks().forEach(t => t.stop()) } catch { /* ya cerrado */ }
      streamRef.current = null; mediaRef.current = null
    }
  }, [])

  // ── Estilos (variables de tema: valen en claro y en oscuro) ─────────────────
  const btn = { width: '32px', height: '32px', borderRadius: '8px', border: '1px solid transparent', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: 0 }
  // B-274 · LA TINTA, NO EL RELLENO. Cinco sitios de este fichero ESCRIBEN con el color de marca
  // sobre una superficie oscura del chat (`--surface` / `--surface-2`): el botón de formato activo,
  // el cartel de soltar ficheros, las iniciales del mencionado, la categoría de emoji elegida y la
  // barra de «Respondiendo a». A todos les toca `--primary-tinta`, la variante clara del color de la
  // clínica. Con `--primary` —que está calibrado para hacer de FONDO con letra blanca encima— la
  // barra de «Respondiendo a» daba 3,27:1 con el azul de Biango y 1,12:1 con una marca granate:
  // invisible. Y esa barra sale A LA VEZ que la cita del mensaje, que ya se arregló: eran gemelas.
  // El relleno se queda en `--primary` donde de verdad rellena (el botón de enviar y el de la nota
  // de voz), con `--sobre-primary` de letra.
  const btnActivo = { ...btn, background: 'var(--surface-2)', color: 'var(--primary-tinta)', borderColor: 'var(--border)' }
  const puedeEnviar = !!String(valor || '').trim() && !deshabilitado
  const restantes = MAX_TEXTO - String(valor || '').length

  return (
    <div
      className="cmpc-raiz"
      onDragEnter={(e) => { if (!traeArchivos(e) || deshabilitado) return; e.preventDefault(); arrastreRef.current++; setArrastrando(true) }}
      onDragOver={(e) => { if (!traeArchivos(e) || deshabilitado) return; e.preventDefault(); e.dataTransfer.dropEffect = 'copy' }}
      onDragLeave={(e) => { if (!traeArchivos(e) || deshabilitado) return; arrastreRef.current = Math.max(0, arrastreRef.current - 1); if (!arrastreRef.current) setArrastrando(false) }}
      onDrop={alSoltar}
      style={{ position: 'relative', flexShrink: 0, background: 'var(--surface)', borderTop: '1px solid var(--border)', padding: '10px 12px', paddingBottom: 'max(10px, var(--sa-abajo))' }}
    >
      <style>{`
        @keyframes cmpcLatido{0%,100%{opacity:1}50%{opacity:.25}}
        .cmpc-b:hover:not(:disabled){background:var(--surface-2);color:var(--text-primary)}
        .cmpc-b:disabled{opacity:.4;cursor:default}
        .cmpc-em{border:none;background:none;cursor:pointer;font-size:20px;line-height:1;padding:5px;border-radius:8px}
        .cmpc-em:hover{background:var(--surface-2)}
        .cmpc-men{display:flex;align-items:center;gap:9px;width:100%;padding:7px 10px;border:none;background:none;cursor:pointer;text-align:left;color:var(--text-primary)}
        .cmpc-men:hover{background:var(--surface-2)}
        .cmpc-ta::placeholder{color:var(--text-muted)}
      `}</style>

      {/* Arrastrar y soltar: el borde resaltado dice dónde hay que soltar */}
      {arrastrando && (
        <div style={{ position: 'absolute', inset: '6px', borderRadius: '14px', border: '2px dashed var(--primary)', background: 'color-mix(in srgb, var(--primary) 10%, var(--surface))', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: 'var(--primary-tinta)', fontWeight: 700, fontSize: '13.5px', zIndex: 20, pointerEvents: 'none' }}>
          <Icon n="clip" s={18} />{i18nT('components.chat.ComposerChat.suelta_el_archivo_para_adjuntarlo', ' Suelta el archivo para adjuntarlo')}
        </div>
      )}

      {/* Lista de @menciones (encima del composer, como en Rocket.Chat) */}
      {mencionAbierta && (
        <div style={{ position: 'absolute', left: '12px', right: '12px', bottom: '100%', marginBottom: '6px', maxHeight: '250px', overflowY: 'auto', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px', boxShadow: 'var(--shadow-lg)', zIndex: 30 }}>
          <div style={{ padding: '7px 12px', fontSize: '11px', fontWeight: 800, letterSpacing: '.4px', textTransform: 'uppercase', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>{i18nT('components.chat.ComposerChat.mencionar_a', 'Mencionar a')}</div>
          {sugeridos.map((u, i) => (
            <button key={u.id} type="button" className="cmpc-men"
              onMouseDown={(e) => { e.preventDefault(); insertarMencion(u) }}
              onMouseEnter={() => setIdxMencion(i)}
              style={{ background: i === idxSel ? 'var(--surface-2)' : 'transparent' }}>
              <span style={{ width: '26px', height: '26px', borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'color-mix(in srgb, var(--primary) 10%, transparent)', color: 'var(--primary-tinta)', fontSize: '11px', fontWeight: 800 }}>
                {u.esTodos ? <IcoFmt n="at" s={14} /> : ((u.nombre || '?').trim()[0]?.toUpperCase() || '?')}
              </span>
              <span style={{ flex: 1, minWidth: 0, fontSize: '13px', fontWeight: u.esTodos ? 800 : 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {u.esTodos ? i18nT('components.chat.ComposerChat.todos', '@todos') : u.nombre}
              </span>
              {/* EL NÚMERO VA AQUÍ, ANTES DE PULSAR: avisar a todo el grupo sin saber a cuánta gente
                  va es justo la forma de mandarlo sin querer. */}
              {u.esTodos && <span style={{ fontSize: '11px', color: 'var(--text-muted)', flexShrink: 0 }}>{tx('components.chat.ComposerChat.avisa_a', { one: 'Avisa a {{n}} persona', other: 'Avisa a {{n}} personas' }, { n: u.n, count: u.n, _forma: (u.n === 1) ? 'one' : 'other' })}</span>}
              {!u.esTodos && u.rol && <span style={{ fontSize: '11px', color: 'var(--text-muted)', flexShrink: 0 }}>{nombreRol(u.rol)}</span>}
            </button>
          ))}
        </div>
      )}

      {/* Panel de emojis propio */}
      {emojisAbierto && (
        <div ref={emojiRef} style={{ position: 'absolute', left: '12px', bottom: '100%', marginBottom: '6px', width: 'min(320px, calc(100% - 24px))', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px', boxShadow: 'var(--shadow-lg)', zIndex: 30, overflow: 'hidden' }}>
          <div style={{ display: 'flex', gap: '2px', padding: '6px', borderBottom: '1px solid var(--border)', overflowX: 'auto' }}>
            {CATEGORIAS.map(([t], i) => (
              <button key={t} type="button" onClick={() => setCatEmoji(i)}
                style={{ border: 'none', cursor: 'pointer', padding: '5px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap', background: catEmoji === i ? 'var(--surface-2)' : 'transparent', color: catEmoji === i ? 'var(--primary-tinta)' : 'var(--text-secondary)' }}>
                {tDato(t)}
              </button>
            ))}
          </div>
          <div style={{ maxHeight: '220px', overflowY: 'auto', padding: '8px' }}>
            {recientes.length > 0 && catEmoji === 0 && (
              <>
                <div style={{ fontSize: '10.5px', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.4px', padding: '2px 4px 6px' }}>{i18nT('components.chat.ComposerChat.recientes', 'Recientes')}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px', marginBottom: '8px' }}>
                  {recientes.map(em => <button key={'r' + em} type="button" className="cmpc-em" onClick={() => elegirEmoji(em)}>{em}</button>)}
                </div>
              </>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px' }}>
              {CATEGORIAS[catEmoji][1].map(em => <button key={em} type="button" className="cmpc-em" onClick={() => elegirEmoji(em)}>{em}</button>)}
            </div>
          </div>
        </div>
      )}

      {/* Barra de "respondiendo a" */}
      {respondiendo && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '7px 10px', marginBottom: '7px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: '10px' }}>
          <span style={{ width: '3px', alignSelf: 'stretch', background: 'var(--primary)', borderRadius: '2px', flexShrink: 0 }} />
          <span style={{ flex: 1, minWidth: 0 }}>
            <b style={{ display: 'block', fontSize: '11.5px', color: 'var(--primary-tinta)' }}>{tx('components.chat.ComposerChat.respondiendo_a', 'Respondiendo a {{x}}', { x: respondiendo.autor || i18nT('components.chat.ComposerChat.un_mensaje', 'un mensaje') })}</b>
            <span style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{extracto(respondiendo)}</span>
          </span>
          <button type="button" className="cmpc-b" onClick={() => cancelarResponder?.()} title={i18nT('components.chat.ComposerChat.quitar_la_respuesta', 'Quitar la respuesta')} aria-label={i18nT('components.chat.ComposerChat.quitar_la_respuesta', 'Quitar la respuesta')} style={btn}><Icon n="close" s={16} /></button>
        </div>
      )}

      <div style={{ border: `1px solid ${arrastrando ? 'var(--primary)' : 'var(--border)'}`, borderRadius: '14px', background: 'var(--surface-2)', overflow: 'hidden' }}>
        {grabando ? (
          // Grabando: la caja de texto se sustituye por el contador, para que no haya duda de qué se envía.
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 12px' }}>
            <span style={{ width: '11px', height: '11px', borderRadius: '50%', background: 'var(--danger)', animation: 'cmpcLatido 1s infinite', flexShrink: 0 }} />
            <span style={{ flex: 1, minWidth: 0, fontSize: '13.5px', fontWeight: 700, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
              {tx('components.chat.ComposerChat.grabando_nota_de_voz', 'Grabando nota de voz · {{mmss}}', { mmss: mmss(segs) })}
            </span>
            <button type="button" className="cmpc-b" onClick={cancelarAudio} title={i18nT('components.chat.ComposerChat.cancelar_la_grabacion', 'Cancelar la grabación')} aria-label={i18nT('components.chat.ComposerChat.cancelar_la_grabacion', 'Cancelar la grabación')} style={btn}><Icon n="trash" s={17} /></button>
            <button type="button" onClick={pararAudio} title={i18nT('components.chat.ComposerChat.enviar_la_nota_de_voz', 'Enviar la nota de voz')} aria-label={i18nT('components.chat.ComposerChat.enviar_la_nota_de_voz', 'Enviar la nota de voz')}
              style={{ width: '38px', height: '38px', borderRadius: '50%', border: 'none', background: 'var(--primary)', color: 'var(--sobre-primary, #fff)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon n="send" s={17} />
            </button>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', padding: '8px 8px 4px 12px' }}>
              <textarea
                ref={taRef}
                className="cmpc-ta"
                value={valor}
                onChange={alEscribir}
                onKeyDown={alPulsarTecla}
                onKeyUp={sincronizarCursor}
                onClick={sincronizarCursor}
                onPaste={alPegar}
                /* La caja NO se bloquea mientras se envía: `deshabilitado` se enciende durante el
                   envío o la subida de un adjunto, y desactivar el <textarea> le quitaba el foco al
                   usuario después de CADA mensaje (había que volver a pinchar para escribir el
                   siguiente) además de soltarle un "no se puede escribir" que no era verdad. Lo que
                   sí se bloquea es enviar: enviar() y el botón ya lo comprueban. */
                rows={1}
                placeholder={respondiendo ? i18nT('components.chat.ComposerChat.escribe_tu_respuesta', 'Escribe tu respuesta…') : i18nT('components.chat.ComposerChat.escribe_un_mensaje', 'Escribe un mensaje…')}
                style={{ flex: 1, minWidth: 0, resize: 'none', border: 'none', outline: 'none', background: 'transparent', color: 'var(--text-primary)', fontSize: '14px', lineHeight: LINEA + 'px', fontFamily: 'inherit', padding: '8px 0', maxHeight: (LINEAS_MAX * LINEA + 20) + 'px' }}
              />
              <button type="button" onClick={enviar} disabled={!puedeEnviar} title={esMovil ? i18nT('components.chat.ComposerChat.enviar', 'Enviar') : i18nT('components.chat.ComposerChat.enviar_enter', 'Enviar (Enter)')} aria-label={i18nT('components.chat.ComposerChat.enviar_mensaje', 'Enviar mensaje')}
                style={{ width: '38px', height: '38px', borderRadius: '50%', border: 'none', flexShrink: 0, marginBottom: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: puedeEnviar ? 'pointer' : 'default', background: puedeEnviar ? 'var(--primary)' : 'var(--surface)', color: puedeEnviar ? 'var(--sobre-primary, #fff)' : 'var(--text-muted)', transition: 'background .12s' }}>
                <Icon n="send" s={17} />
              </button>
            </div>

            {/* Barra de herramientas */}
            {/* overflowX: en un móvil estrecho la barra se desliza en vez de romper la caja */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px', padding: '2px 6px 6px', overflowX: 'auto' }}>
              <button ref={btnEmojiRef} type="button" className="cmpc-b" disabled={deshabilitado} onClick={() => setEmojisAbierto(v => !v)} title={i18nT('components.chat.ComposerChat.emoji', 'Emoji')} aria-label={i18nT('components.chat.ComposerChat.emoji', 'Emoji')} style={emojisAbierto ? btnActivo : btn}><IcoFmt n="smile" /></button>
              <span style={{ width: '1px', height: '18px', background: 'var(--border)', margin: '0 4px', flexShrink: 0 }} />
              <button type="button" className="cmpc-b" disabled={deshabilitado} onClick={() => envolver('**')} title={i18nT('components.chat.ComposerChat.negrita_ctrl_b', 'Negrita (Ctrl+B)')} aria-label={i18nT('components.chat.ComposerChat.negrita', 'Negrita')} style={btn}><IcoFmt n="bold" /></button>
              <button type="button" className="cmpc-b" disabled={deshabilitado} onClick={() => envolver('_')} title={i18nT('components.chat.ComposerChat.cursiva_ctrl_i', 'Cursiva (Ctrl+I)')} aria-label={i18nT('components.chat.ComposerChat.cursiva', 'Cursiva')} style={btn}><IcoFmt n="italic" /></button>
              <button type="button" className="cmpc-b" disabled={deshabilitado} onClick={() => envolver('~~')} title={i18nT('components.chat.ComposerChat.tachado', 'Tachado')} aria-label={i18nT('components.chat.ComposerChat.tachado', 'Tachado')} style={btn}><IcoFmt n="strike" /></button>
              <button type="button" className="cmpc-b" disabled={deshabilitado} onClick={() => envolver('`')} title={i18nT('components.chat.ComposerChat.codigo', 'Código')} aria-label={i18nT('components.chat.ComposerChat.codigo', 'Código')} style={btn}><IcoFmt n="code" /></button>
              <span style={{ width: '1px', height: '18px', background: 'var(--border)', margin: '0 4px', flexShrink: 0 }} />
              <button type="button" className="cmpc-b" disabled={deshabilitado} onClick={abrirMenciones} title={i18nT('components.chat.ComposerChat.mencionar_a_alguien', 'Mencionar a alguien')} aria-label={i18nT('components.chat.ComposerChat.mencionar_a_alguien', 'Mencionar a alguien')} style={btn}><IcoFmt n="at" /></button>
              <button type="button" className="cmpc-b" disabled={deshabilitado} onClick={() => fileRef.current?.click()} title={i18nT('components.chat.ComposerChat.adjuntar_archivo', 'Adjuntar archivo')} aria-label={i18nT('components.chat.ComposerChat.adjuntar_archivo', 'Adjuntar archivo')} style={btn}><Icon n="clip" s={18} /></button>
              {soportaAudio && (
                <button type="button" className="cmpc-b" disabled={deshabilitado} onClick={empezarAudio} title={i18nT('components.chat.ComposerChat.nota_de_voz_se_transcribe', 'Nota de voz (se transcribe sola)')} aria-label={i18nT('components.chat.ComposerChat.nota_de_voz', 'Nota de voz')} style={btn}><Icon n="mic" s={18} /></button>
              )}
              <span style={{ flex: 1 }} />
              {restantes <= 500 && (
                <span style={{ fontSize: '11px', fontWeight: 700, color: restantes <= 0 ? 'var(--danger-tinta)' : 'var(--text-muted)', fontVariantNumeric: 'tabular-nums', marginRight: '6px' }}>{restantes}</span>
              )}
              {!esMovil && <span style={{ fontSize: '11px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{i18nT('components.chat.ComposerChat.enter_envia_mayus_enter_salta', 'Enter envía · Mayús+Enter salta de línea')}</span>}
            </div>
          </>
        )}
      </div>

      {/* SIN `accept` a propósito (T6): el servidor ya no va por lista blanca, así que un filtro
          aquí solo serviría para esconder en el explorador de archivos justo los tipos que ahora sí
          se pueden mandar (Word, Excel, ZIP, vídeo, DICOM…). Lo que NO se admite (programas y
          scripts) lo rechaza el backend con un mensaje que explica por qué. */}
      <input ref={fileRef} type="file" multiple style={{ display: 'none' }}
        onChange={(e) => { subir(e.target.files); e.target.value = '' }} />
    </div>
  )
}
